import { useEffect, useRef, useCallback, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ClientLocation, LocationType } from '@/api/client-locations';
import { LOCATION_TYPE_COLORS } from '@/api/client-locations';
import { getMapStyleUrl } from '@/api/geo';

export interface EditPinState {
  lat: number;
  lon: number;
}

interface MapViewProps {
  center: { lat: number; lon: number };
  locations: ClientLocation[];
  clientLocation: { lat: number; lon: number } | null;
  selectedLocationId?: string;
  selectedLocation?: ClientLocation | null;
  onMarkerClick?: (location: ClientLocation) => void;
  // Edit mode
  editMode?: 'add' | 'edit' | null;
  editPinPosition?: EditPinState | null;
  onEditPinChange?: (position: EditPinState) => void;
  editPinType?: LocationType;
}

// Marker colors
const MARKER_COLORS: Record<LocationType | 'CLIENT' | 'EDIT', string> = {
  PARKING: LOCATION_TYPE_COLORS.PARKING,
  ENTRANCE: LOCATION_TYPE_COLORS.ENTRANCE,
  CLIENT: '#ef4444', // red for main client marker
  EDIT: '#3b82f6', // blue for edit pin
};

export function MapView({
  center,
  locations,
  clientLocation,
  selectedLocationId,
  selectedLocation,
  onMarkerClick,
  editMode,
  editPinPosition,
  onEditPinChange,
  editPinType,
}: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<Map<string, maplibregl.Marker>>(new Map());
  const clientMarker = useRef<maplibregl.Marker | null>(null);
  const editMarker = useRef<maplibregl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [show3DBuildings, setShow3DBuildings] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: getMapStyleUrl(),
      center: [center.lon, center.lat],
      zoom: 15,
      attributionControl: false,
    });

    map.current.on('load', () => {
      setMapLoaded(true);
      // Hide 3D buildings by default
      if (map.current?.getLayer('building-3d')) {
        map.current.setLayoutProperty('building-3d', 'visibility', 'none');
      }
    });

    // Add navigation controls
    map.current.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      'top-right'
    );

    // Add geolocation control
    map.current.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
      'top-right'
    );

    return () => {
      map.current?.remove();
      map.current = null;
      setMapLoaded(false);
    };
  }, []);

  // Toggle 3D buildings visibility
  const handleToggle3DBuildings = useCallback(() => {
    if (!map.current || !mapLoaded) return;

    const newShow3D = !show3DBuildings;
    setShow3DBuildings(newShow3D);

    if (map.current.getLayer('building-3d')) {
      map.current.setLayoutProperty(
        'building-3d',
        'visibility',
        newShow3D ? 'visible' : 'none'
      );

      // Also adjust pitch for better 3D view
      map.current.easeTo({
        pitch: newShow3D ? 45 : 0,
        duration: 300,
      });
    }
  }, [show3DBuildings, mapLoaded]);

  // Handle map clicks in edit mode - move the edit pin
  useEffect(() => {
    if (!map.current) return;

    const handleClick = (e: maplibregl.MapMouseEvent) => {
      if (editMode && onEditPinChange) {
        onEditPinChange({ lat: e.lngLat.lat, lon: e.lngLat.lng });
      }
    };

    map.current.on('click', handleClick);
    return () => {
      map.current?.off('click', handleClick);
    };
  }, [editMode, onEditPinChange]);

  // Update center when it changes
  useEffect(() => {
    if (!map.current) return;
    map.current.flyTo({
      center: [center.lon, center.lat],
      zoom: 15,
      duration: 1000,
    });
  }, [center.lat, center.lon]);

  // Create marker element
  // showAsPin: true for draggable edit markers AND for selected markers
  const createMarkerElement = useCallback(
    (type: LocationType | 'CLIENT' | 'EDIT', isSelected: boolean, isDraggable: boolean = false) => {
      const el = document.createElement('div');
      const color = MARKER_COLORS[type];
      const size = type === 'CLIENT' ? 32 : type === 'EDIT' ? 36 : isSelected ? 28 : 24;
      const pinHeight = 12; // Triangle height

      // Show as pin for draggable (edit mode) OR selected markers
      const showAsPin = isDraggable || isSelected;

      if (showAsPin) {
        // Pin marker with triangle pointing down - anchor will be at bottom (tip)
        el.style.cssText = `
          display: flex;
          flex-direction: column;
          align-items: center;
        `;
        el.innerHTML = `
          <div style="
            width: ${size}px;
            height: ${size}px;
            background-color: ${color};
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: ${isDraggable ? 'grab' : 'pointer'};
            ${isDraggable ? 'animation: pulse 1.5s infinite;' : ''}
          "></div>
          <div style="
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: ${pinHeight}px solid ${color};
            margin-top: -2px;
          "></div>
        `;
      } else {
        // Regular circular marker
        el.innerHTML = `
          <div style="
            width: ${size}px;
            height: ${size}px;
            background-color: ${color};
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: pointer;
          "></div>
        `;
      }

      // Add pulse animation styles
      if (isDraggable && !document.getElementById('pulse-animation')) {
        const style = document.createElement('style');
        style.id = 'pulse-animation';
        style.textContent = `
          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
            70% { box-shadow: 0 0 0 15px rgba(59, 130, 246, 0); }
            100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
          }
        `;
        document.head.appendChild(style);
      }

      return el;
    },
    []
  );

  // Update client marker
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove old client marker
    if (clientMarker.current) {
      clientMarker.current.remove();
      clientMarker.current = null;
    }

    // Add new client marker
    if (clientLocation) {
      const el = createMarkerElement('CLIENT', false);
      clientMarker.current = new maplibregl.Marker({ element: el })
        .setLngLat([clientLocation.lon, clientLocation.lat])
        .setPopup(new maplibregl.Popup().setHTML('<b>Основной адрес</b>'))
        .addTo(map.current);
    }
  }, [clientLocation, createMarkerElement, mapLoaded]);

  // Update edit marker
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove old edit marker
    if (editMarker.current) {
      editMarker.current.remove();
      editMarker.current = null;
    }

    // Add edit marker if in edit mode and position is set
    if (editMode && editPinPosition) {
      const markerType = editPinType || 'EDIT';
      const el = createMarkerElement(markerType, true, true);

      editMarker.current = new maplibregl.Marker({
        element: el,
        draggable: true,
        anchor: 'bottom', // Pin tip points to exact coordinates
      })
        .setLngLat([editPinPosition.lon, editPinPosition.lat])
        .addTo(map.current);

      // Handle drag end
      editMarker.current.on('dragend', () => {
        const lngLat = editMarker.current?.getLngLat();
        if (lngLat && onEditPinChange) {
          onEditPinChange({ lat: lngLat.lat, lon: lngLat.lng });
        }
      });

      // Update cursor during drag
      const circleEl = el.querySelector('div') as HTMLElement;
      if (circleEl) {
        el.addEventListener('mousedown', () => {
          circleEl.style.cursor = 'grabbing';
        });
        el.addEventListener('mouseup', () => {
          circleEl.style.cursor = 'grab';
        });
      }
    }
  }, [editMode, editPinPosition, editPinType, createMarkerElement, onEditPinChange, mapLoaded]);

  // Update location markers
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Remove old markers that are no longer in locations
    const currentIds = new Set(locations.map((l) => l.id));
    markers.current.forEach((marker, id) => {
      if (!currentIds.has(id)) {
        marker.remove();
        markers.current.delete(id);
      }
    });

    // Add or update markers
    locations.forEach((location) => {
      const isSelected = location.id === selectedLocationId;
      // Hide selected marker in edit mode (edit pin takes over)
      const isHiddenForEdit = editMode === 'edit' && isSelected;

      const existingMarker = markers.current.get(location.id);

      if (isHiddenForEdit) {
        // Hide marker when editing
        if (existingMarker) {
          existingMarker.remove();
          markers.current.delete(location.id);
        }
        return;
      }

      if (existingMarker) {
        // Remove and recreate marker to update selection state
        // (replaceWith breaks MapLibre's internal element reference)
        existingMarker.remove();
        markers.current.delete(location.id);
      }

      {
        // Create new marker
        const el = createMarkerElement(location.type, isSelected);
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          onMarkerClick?.(location);
        });

        // Use 'bottom' anchor for selected (pin-style) markers so tip points to coordinates
        const marker = new maplibregl.Marker({
          element: el,
          anchor: isSelected ? 'bottom' : 'center',
        })
          .setLngLat([location.longitude, location.latitude])
          .addTo(map.current!);

        markers.current.set(location.id, marker);
      }
    });
  }, [locations, selectedLocationId, editMode, createMarkerElement, onMarkerClick, mapLoaded]);

  // Fly to selected location
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (!selectedLocation || editMode) return;

    // Fly to location
    map.current.flyTo({
      center: [selectedLocation.longitude, selectedLocation.latitude],
      zoom: 15,
      duration: 500,
    });
  }, [selectedLocation, editMode, mapLoaded]);

  // Fly to edit pin when it moves
  useEffect(() => {
    if (!map.current || !editMode || !editPinPosition) return;

    // Smooth pan to keep pin in view
    const bounds = map.current.getBounds();
    const point = new maplibregl.LngLat(editPinPosition.lon, editPinPosition.lat);

    if (!bounds.contains(point)) {
      map.current.panTo([editPinPosition.lon, editPinPosition.lat], { duration: 300 });
    }
  }, [editPinPosition, editMode]);

  return (
    <div className="relative w-full h-full" style={{ minHeight: '300px' }}>
      <div ref={mapContainer} className="w-full h-full" />

      {/* 3D buildings toggle - positioned below standard controls */}
      <div className="absolute right-2 top-28 z-10">
        <Button
          size="icon"
          variant={show3DBuildings ? 'default' : 'secondary'}
          className="h-8 w-8 shadow-md"
          onClick={handleToggle3DBuildings}
          title={show3DBuildings ? 'Скрыть 3D здания' : 'Показать 3D здания'}
        >
          <Box className="w-4 h-4" />
        </Button>
      </div>

      {/* Selected location info - bottom overlay */}
      {selectedLocation && !editMode && (
        <div className="absolute bottom-2 left-2 right-2 z-10">
          <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md border">
            <div className="font-medium text-sm">{selectedLocation.name || 'Без названия'}</div>
            {selectedLocation.note && (
              <div className="text-xs text-muted-foreground italic mt-0.5">{selectedLocation.note}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
